import type { PickupsSummary, SummaryArea } from "./api";
import { dateText, galWhole } from "./overview";
import { inset, type Placed, type Rect, squarify } from "./treemap";

export const PERIODS = [4, 13, 52] as const;
export type Period = (typeof PERIODS)[number];
export const PERIOD_DEFAULT: Period = 13;
export const AREA_COLOURS = 8;

/** The period of the `weeks` search value, or the default. */
export function periodFromSearch(search: string): Period {
  const found = Number(new URLSearchParams(search).get("weeks"));
  return (PERIODS as readonly number[]).includes(found) ? (found as Period) : PERIOD_DEFAULT;
}

/** The search with the period that the page uses, or null when the URL already has it. */
export function periodSearch(search: string, period: Period): string | null {
  const params = new URLSearchParams(search);
  if (!params.has("weeks") || params.get("weeks") === String(period)) return null;
  params.set("weeks", String(period));
  return `?${params.toString()}`;
}

/** "Mon Jul 27, 2026 to Fri Oct 23, 2026": a range with the year, so 52 weeks do not read backwards. */
export const rangeText = (since: string, day: string) => `${dateText(since)}, ${since.slice(0, 4)} to ${dateText(day)}, ${day.slice(0, 4)}`;

export interface Headline {
  pickups: number;
  gal: number;
  accounts: number;
  average: number | null;
}

/** The pickups, the gallons, the accounts and the average gallons of one pickup. */
export function headline(summary: PickupsSummary): Headline {
  return {
    pickups: summary.count,
    gal: summary.gal,
    accounts: summary.accounts,
    average: summary.count > 0 ? summary.gal / summary.count : null,
  };
}

export const areaName = (area: SummaryArea) => area.name ?? "No area";

export interface Leaf {
  key: string;
  label: string;
  short?: string;
  gal: number;
  accountId: string | null;
  /** The p50 fill of now; null with no estimate, undefined from an API before roadmap 101. */
  fill?: number | null;
}

export interface AreaBox {
  area: SummaryArea;
  colour: number;
  box: Rect;
  label: boolean;
  leaves: Placed<Leaf>[];
}

const HEADER = 18;

/** The accounts of one area, and one leaf for the accounts that the reply does not list. */
export function leavesOf(area: SummaryArea): Leaf[] {
  const leaves: Leaf[] = area.accounts.map((a) => ({ key: a.account_id, label: `${a.number} ${a.name}`, short: a.number, gal: a.gal, accountId: a.account_id, fill: a.fill_share }));
  if (area.rest.gal > 0) {
    const text = `${area.rest.accounts.toLocaleString("en-US")} more ${area.rest.accounts === 1 ? "account" : "accounts"}`;
    leaves.push({ key: `${area.area_id ?? "none"}/rest`, label: text, gal: area.rest.gal, accountId: null });
  }
  return leaves;
}

/** Two rectangles share a part of an edge longer than `gap`. */
export function touches(a: Rect, b: Rect, gap = 1): boolean {
  const across = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  const along = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const sideBySide = Math.abs(a.x + a.w - b.x) <= gap || Math.abs(b.x + b.w - a.x) <= gap;
  const stacked = Math.abs(a.y + a.h - b.y) <= gap || Math.abs(b.y + b.h - a.y) <= gap;
  return (sideBySide && across > gap) || (stacked && along > gap);
}

/** A colour for each rectangle that no touching rectangle has, if one is free; else the least used one. */
export function colourAreas(boxes: readonly Rect[], count = AREA_COLOURS): number[] {
  const found: number[] = [];
  const uses = Array.from({ length: count }, () => 0);
  boxes.forEach((box, n) => {
    const near = Array.from({ length: count }, () => 0);
    for (let k = 0; k < n; k += 1) if (touches(box, boxes[k])) near[found[k]] += 1;
    let best = 0;
    for (let c = 1; c < count; c += 1) if (near[c] < near[best] || (near[c] === near[best] && uses[c] < uses[best])) best = c;
    found.push(best);
    uses[best] += 1;
  });
  return found;
}

const CHAR_PX = 7;
const PAD_PX = 12;

/** The longest of the texts that fits in this width, or null. A text is never cut. */
export function fitText(width: number, ...texts: (string | undefined)[]): string | null {
  return texts.find((text): text is string => text !== undefined && text.length * CHAR_PX + PAD_PX <= width) ?? null;
}

/** The areas, then the accounts in each area, as nested rectangles in a box of this size. */
export function layout(summary: Pick<PickupsSummary, "areas">, width: number, height: number, colour?: (area: SummaryArea) => number | undefined): AreaBox[] {
  const placed = squarify(summary.areas, (area) => area.gal, { x: 0, y: 0, w: width, h: height });
  const colours = colourAreas(placed);
  return placed.map(({ item, ...box }, n) => {
    const label = box.w >= 40 && box.h >= HEADER + 12;
    return {
      area: item,
      colour: colour?.(item) ?? colours[n],
      box,
      label,
      leaves: squarify(leavesOf(item), (leaf) => leaf.gal, inset(box, 1, label ? HEADER : 0)),
    };
  });
}

/** The words of a leaf fit when the rectangle is this large. */
export const fits = (rect: Rect) => rect.w >= 64 && rect.h >= 30;

/** Rule U-32: a small leaf has no padding and a tiny leaf has no border. Thus each box keeps its dimensions. */
export const leafSize = (rect: Rect): "" | " small" | " tiny" => (fits(rect) ? "" : Math.min(rect.w, rect.h) < 3 ? " tiny" : " small");

/** A share as a whole percent, or "<1%". */
export function shareText(part: number, whole: number): string {
  if (whole <= 0) return "0%";
  const percent = Math.round((part / whole) * 100);
  return percent < 1 && part > 0 ? "<1%" : `${percent}%`;
}

/** "1% of all gallons": the share of the gallons of all areas in the period. */
export const shareOfAllText = (part: number, whole: number) => `${shareText(part, whole)} of all gallons`;

/** The caption under the treemap: a phone has no Ctrl key and no Escape key. */
/** The help under the treemap; `more` names the "N more" box, for a reply that does not list each account. */
export const treemapHelp = (touch: boolean, more = false) =>
  touch
    ? `Tap a rectangle once to see its account and gallons, and again to open it. Tap an area name${more ? " or “N more”" : ""} to zoom in. Pinch zooms; the links above the treemap go back.`
    : `Point at a rectangle to see its account and gallons. A click opens the account; a click on an area name${more ? " or on “N more”" : ""} zooms in. Ctrl + scroll zooms around the pointer, and Escape goes back.`;

export interface TopRow {
  accountId: string;
  label: string;
  area: string;
  gal: number;
}

/** The accounts with the most gallons, for the text that goes with the treemap. */
export function topAccounts(summary: PickupsSummary, count = 10): TopRow[] {
  return summary.areas
    .flatMap((area) => area.accounts.map((a) => ({ accountId: a.account_id, label: `${a.number} ${a.name}`, area: areaName(area), gal: a.gal })))
    .sort((a, b) => b.gal - a.gal || a.label.localeCompare(b.label))
    .slice(0, count);
}

/** The words of the weekly chart for a screen reader, the oldest week first. */
export function weeksText(summary: PickupsSummary): string {
  const last = summary.weeks.length - 1;
  return summary.weeks
    .map((week, n) => `week from ${dateText(week.start)}${n === last ? ` to ${dateText(summary.day)}` : ""}: ${galWhole(week.gal)}`)
    .join("; ");
}

export const NO_AREA = "none";
export const areaKey = (area: Pick<SummaryArea, "area_id">) => area.area_id ?? NO_AREA;

/** One level of the treemap zoom: one area, or the accounts of the area that the summary does not list. */
export interface ZoomStep {
  area: string;
  rest: boolean;
}

/** The zoom stack after a click on an area name (rest false) or on its "N more accounts" box. */
export const zoomTo = (area: string, rest: boolean): ZoomStep[] => (rest ? [{ area, rest: false }, { area, rest: true }] : [{ area, rest: false }]);

const NO_REST = { accounts: 0, gal: 0 };

/** The areas that the treemap shows at the top of the stack. `full` is the reply with all accounts of that area. */
export function zoomAreas(summary: PickupsSummary, stack: readonly ZoomStep[], full?: SummaryArea | null): SummaryArea[] {
  const step = stack[stack.length - 1];
  const area = step && summary.areas.find((a) => areaKey(a) === step.area);
  if (!step || !area) return summary.areas;
  const all = full && areaKey(full) === step.area && full.accounts.length >= area.accounts.length ? full : null;
  if (!step.rest) return [all ? { ...area, accounts: all.accounts, rest: all.rest } : area];
  if (!all) return [{ ...area, gal: area.rest.gal, account_count: area.rest.accounts, accounts: [], rest: area.rest }];
  const listed = new Set(area.accounts.map((a) => a.account_id));
  const accounts = all.accounts.filter((a) => !listed.has(a.account_id));
  return [{ ...area, gal: area.rest.gal, account_count: area.rest.accounts, accounts, rest: all.rest.gal > 0 ? all.rest : NO_REST }];
}

/** The words of each level of the stack, "All areas" first. */
export function zoomTrail(summary: PickupsSummary, stack: readonly ZoomStep[]): string[] {
  const trail = ["All areas"];
  for (const step of stack) {
    const area = summary.areas.find((a) => areaKey(a) === step.area);
    if (!area) break;
    trail.push(step.rest ? `${area.rest.accounts.toLocaleString("en-US")} more` : `${areaName(area)} · ${galWhole(area.gal)}`);
  }
  return trail;
}

/** The stack without the steps whose area is not in the summary. */
export const keptStack = (summary: PickupsSummary, stack: readonly ZoomStep[]) => {
  const n = stack.findIndex((step) => !summary.areas.some((a) => areaKey(a) === step.area));
  return n < 0 ? [...stack] : stack.slice(0, n);
};

/** The scale of the treemap and the top left corner of the box in the scaled layout. */
export interface View {
  s: number;
  x: number;
  y: number;
}

export const NO_ZOOM: View = { s: 1, x: 0, y: 0 };
export const SCALE_MAX = 12;
export const STEP_IN = 3;
const STEP_OUT = 0.7;

const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

/** The view in the bounds: the scaled layout always covers the box. */
export const clampView = (view: View, width: number, height: number): View => ({
  s: view.s,
  x: clamp(view.x, 0, width * (view.s - 1)),
  y: clamp(view.y, 0, height * (view.s - 1)),
});

/** The view after a zoom by `factor`, with the point (cx, cy) of the box kept under the pointer. */
export function zoomAround(view: View, factor: number, cx: number, cy: number, width: number, height: number): View {
  const s = clamp(view.s * factor, 1, SCALE_MAX);
  const ux = (cx + view.x) / view.s;
  const uy = (cy + view.y) / view.s;
  return clampView({ s, x: ux * s - cx, y: uy * s - cy }, width, height);
}

/** The zoom factor of one wheel event; deltaMode 1 counts lines, not pixels. */
export const wheelFactor = (deltaY: number, deltaMode = 0) => Math.exp(-clamp(deltaY * (deltaMode === 1 ? 33 : 1), -200, 200) * 0.0025);

/** The area box under the point (cx, cy) of the box, in a layout made at this view. */
export const areaAt = (areas: readonly AreaBox[], view: View, cx: number, cy: number) => {
  const x = cx + view.x;
  const y = cy + view.y;
  return areas.find(({ box }) => x >= box.x && x < box.x + box.w && y >= box.y && y < box.y + box.h) ?? null;
};

export interface Zoom {
  stack: ZoomStep[];
  view: View;
  /** The zoom out that is not used yet, at scale 1: a small wheel turn does not go up a level. */
  out: number;
}

export const NO_ZOOM_STACK: Zoom = { stack: [], view: NO_ZOOM, out: 1 };

/** One ctrl+wheel or pinch step: STEP_IN at all areas goes into the area under the pointer, STEP_OUT at scale 1 goes up. */
export function zoomStep(zoom: Zoom, factor: number, cx: number, cy: number, width: number, height: number, areas: readonly AreaBox[]): Zoom {
  if (factor < 1 && zoom.view.s <= 1 + 1e-6) {
    const out = zoom.out * factor;
    if (out > STEP_OUT || !zoom.stack.length) return { ...zoom, out: zoom.stack.length ? out : 1 };
    return { stack: zoom.stack.slice(0, -1), view: NO_ZOOM, out: 1 };
  }
  const view = zoomAround(zoom.view, factor, cx, cy, width, height);
  if (!zoom.stack.length && view.s >= STEP_IN) {
    const hit = areaAt(areas, zoom.view, cx, cy);
    if (hit && areas.length > 1) return { stack: zoomTo(areaKey(hit.area), false), view: NO_ZOOM, out: 1 };
  }
  return { ...zoom, view, out: 1 };
}

/** One level up: first back to scale 1, then the level before. */
export const zoomUp = (zoom: Zoom): Zoom => (zoom.view.s > 1 ? { ...zoom, view: NO_ZOOM, out: 1 } : { stack: zoom.stack.slice(0, -1), view: NO_ZOOM, out: 1 });

/** True when the reply has the fill field, so the page shows the shading and its key. */
export const hasFill = (areas: readonly SummaryArea[]) => areas.some((area) => area.accounts.some((a) => a.fill_share !== undefined));
